import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { post, patch, del } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { fmtPct } from '../lib/format';
import { Plus, Trash2, Pencil, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const blank = () => ({
  name: '', slug: '', ownership_pct: 100, description: '', color: '#D4AF37',
});

export default function Empresas() {
  const { companies, reloadCompanies } = useApp();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());

  const openNew = () => { setEditId(null); setForm(blank()); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, slug: c.slug, ownership_pct: c.ownership_pct, description: c.description || '', color: c.color || '#D4AF37' });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nome obrigatório');
    const slug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const pct = parseFloat(String(form.ownership_pct).replace(',', '.')) || 0;
    const payload = { ...form, slug, ownership_pct: pct, description: form.description || null };
    try {
      if (editId) {
        await patch(`/companies/${editId}`, payload);
      } else {
        await post('/companies', payload);
      }
      toast.success(editId ? 'Atualizada' : 'Criada');
      setOpen(false);
      reloadCompanies();
    } catch (e) {
      toast.error('Erro ao salvar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir empresa?')) return;
    await del(`/companies/${id}`);
    toast.success('Excluída');
    reloadCompanies();
  };

  const totalGroup = companies.reduce((s, c) => s + c.ownership_pct, 0) / (companies.length || 1);

  return (
    <div className="fade-in">
      <TopBar
        title="Empresas / Unidades de Negócio"
        subtitle={`${companies.length} empresas cadastradas | Participação média: ${fmtPct(totalGroup)}`}
        action={
          <Button onClick={openNew} className="btn-gold rounded-md gap-1.5 px-4" data-testid="btn-new-company">
            <Plus size={15} /> Nova Empresa
          </Button>
        }
      />
      <div className="px-8 py-7 space-y-5">
        <SectionCard padded={false} testid="companies-table">
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Slug</th>
                  <th>Descrição</th>
                  <th className="text-right">% Participação Grupo</th>
                  <th className="text-center">Cor</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} style={{ color: c.color }} />
                        <span className="text-zinc-100 font-medium">{c.name}</span>
                      </div>
                    </td>
                    <td className="font-mono text-zinc-400 text-xs">{c.slug}</td>
                    <td className="text-zinc-400 text-xs">{c.description || '—'}</td>
                    <td className="text-right font-mono gold-text font-semibold">{fmtPct(c.ownership_pct)}</td>
                    <td className="text-center">
                      <span className="inline-block w-5 h-5 rounded border border-zinc-700" style={{ background: c.color }} />
                    </td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100" data-testid={`btn-edit-company-${c.id}`}>
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400" data-testid={`btn-delete-company-${c.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" data-testid="input-company-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Slug</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="ex: minha-empresa" data-testid="input-company-slug" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">% Participação Grupo</Label>
              <Input value={form.ownership_pct} onChange={(e) => setForm({ ...form, ownership_pct: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" placeholder="100" data-testid="input-company-pct" />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" rows={2} />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cor</Label>
              <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 h-10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-company">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
